import React, { useState, useRef, useEffect } from 'react';
import {
  Button,
  makeStyles,
  tokens,
  Title2,
  Body1,
  Spinner,
} from '@fluentui/react-components';
import { EyeRegular, EyeOffRegular, PersonRegular } from '@fluentui/react-icons';
import { useAuth } from '../hooks/useAuth';

const truCareBlue = '#0057B8';
const truCareRed = '#C8102E';

const useStyles = makeStyles({
  background: {
    minHeight: '100vh',
    width: '100vw',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: `linear-gradient(135deg, ${truCareBlue} 0%, #fff 50%, ${truCareRed} 100%)`,
    backgroundSize: '400% 400%',
    transition: 'background 0.5s',
  },
  logo: {
    width: '120px',
    maxHeight: '60px',
    objectFit: 'contain',
    filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.15))',
    transition: 'transform 0.3s ease, filter 0.3s ease',
    ':hover': {
      transform: 'scale(1.05)',
      filter: 'drop-shadow(0 12px 32px rgba(0,0,0,0.2))',
    },
  },
  card: {
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(20px)',
    padding: '48px 40px 40px 40px',
    borderRadius: '24px',
    boxShadow: '0 20px 60px rgba(0,87,184,0.15), 0 8px 24px rgba(200,16,46,0.1)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    maxWidth: '420px',
    minWidth: '320px',
    transition: 'all 0.3s ease',
    position: 'relative',
    willChange: 'transform',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    ':hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 24px 80px rgba(0,87,184,0.2), 0 12px 32px rgba(200,16,46,0.15)',
    },
  },
  title: {
    marginBottom: '12px',
    textAlign: 'center',
    color: truCareBlue,
    fontWeight: 800,
    fontSize: '28px',
    letterSpacing: '-0.5px',
    background: `linear-gradient(135deg, ${truCareBlue} 0%, ${truCareRed} 100%)`,
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  subtitle: {
    marginBottom: '32px',
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    fontSize: '16px',
    lineHeight: '1.5',
  },
  form: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '20px',
    maxWidth: '100%',
  },
  inputWrapper: {
    width: '100%',
    position: 'relative',
    marginBottom: '4px',
    maxWidth: '100%',
    marginTop: '8px',
  },
  floatingLabel: {
    position: 'absolute',
    left: '16px',
    top: '-25px',
    color: tokens.colorNeutralForeground3,
    pointerEvents: 'none',
    fontSize: '14px',
    background: 'transparent',
    padding: '0 6px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    zIndex: 2,
    fontWeight: 500,
  },
  floatingLabelActive: {
    top: '-25px',
    left: '16px',
    fontSize: '14px',
    color: truCareBlue,
    background: 'transparent',
    fontWeight: 600,
    transform: 'scale(0.9)',
    padding: '0 6px',
  },
  input: {
    width: '100%',
    padding: '18px 48px 18px 16px',
    fontSize: '16px',
    borderRadius: '12px',
    border: `2px solid ${tokens.colorNeutralStroke2}`,
    outline: 'none',
    transition: 'all 0.3s ease',
    background: 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(10px)',
    boxSizing: 'border-box',
    height: '56px',
    ':focus': {
      border: `2px solid ${truCareBlue}`,
      boxShadow: `0 0 0 4px rgba(0, 87, 184, 0.1)`,
      background: 'rgba(255, 255, 255, 0.95)',
    },
    ':invalid': {
      border: `2px solid ${truCareRed}`,
      boxShadow: `0 0 0 4px rgba(200, 16, 46, 0.1)`,
    },
    ':hover': {
      border: `2px solid ${truCareBlue}`,
      background: 'rgba(255, 255, 255, 0.9)',
    },
  },
  passwordToggle: {
    position: 'absolute',
    right: '16px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    zIndex: 3,
    color: truCareBlue,
    padding: '8px',
    borderRadius: '8px',
    transition: 'all 0.2s ease',
    ':hover': {
      background: 'rgba(0, 87, 184, 0.1)',
      transform: 'translateY(-50%) scale(1.1)',
    },
  },
  signInButton: {
    width: '100%',
    height: '56px',
    fontSize: '18px',
    fontWeight: 700,
    borderRadius: '12px',
    transition: 'all 0.3s ease',
    ':hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 24px rgba(0,87,184,0.3)',
    },
  },
  error: {
    color: truCareRed,
    fontSize: '14px',
    textAlign: 'center',
    marginBottom: '16px',
    padding: '8px 12px',
    backgroundColor: 'rgba(200, 16, 46, 0.1)',
    borderRadius: '8px',
    border: '1px solid rgba(200, 16, 46, 0.2)',
  },
  loadingSpinner: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
  },
});

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Login: React.FC = () => {
  const styles = useStyles();
  const { login, loading: authLoading } = useAuth();
  const [email, setEmail] = useState(() => localStorage.getItem('trucare_email') || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  // Focus email on mount
  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  // Save email to localStorage
  useEffect(() => {
    if (emailRegex.test(email)) {
      localStorage.setItem('trucare_email', email);
    }
  }, [email]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    console.log('Sign-in attempt started');
    
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address.');
      emailRef.current?.focus();
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      passwordRef.current?.focus();
      return;
    }
    
    setLoading(true);
    console.log('🔍 Starting Microsoft authentication with email:', email);
    
    try {
      await login(email);
      console.log('✅ Login successful - redirecting to dashboard');
    } catch (err) {
      console.error('❌ Login failed:', err);
      setError('Sign-in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Show loading spinner while auth is being checked
  if (authLoading) {
    return (
      <div className={styles.loadingSpinner}>
        <Spinner size="large" label="Checking authentication..." />
      </div>
    );
  }

  return (
    <div className={styles.background}>
      <div className={styles.card}>
        <img src={process.env.PUBLIC_URL + '/color-trucare.jpg'} alt="TruCare Logo" className={styles.logo} />
        <Title2 className={styles.title}>Sign in to TruCare Portal</Title2>
        <Body1 className={styles.subtitle}>Welcome! Please enter your email and password to continue.</Body1>
        
        {error && <div className={styles.error}>{error}</div>}
        
        <form className={styles.form} onSubmit={handleSignIn} autoComplete="on">
          <div className={styles.inputWrapper}>
            <input
              ref={emailRef}
              id="email"
              className={styles.input}
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              autoComplete="email"
              aria-label="Email Address"
              style={{ border: error && !emailRegex.test(email) ? `1px solid ${truCareRed}` : undefined }}
            />
            <span className={styles.floatingLabel + (email ? ' ' + styles.floatingLabelActive : '')}>
              Email Address
            </span>
          </div>
          
          <div className={styles.inputWrapper}>
            <input
              ref={passwordRef}
              id="password"
              className={styles.input}
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              minLength={6}
              autoComplete="current-password"
              aria-label="Password"
              style={{ border: error && password.length < 6 ? `1px solid ${truCareRed}` : undefined }}
            />
            <span className={styles.floatingLabel + (password ? ' ' + styles.floatingLabelActive : '')}>
              Password
            </span>
            <button
              type="button"
              className={styles.passwordToggle}
              tabIndex={0}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              onClick={() => setShowPassword(v => !v)}
            >
              {showPassword ? <EyeOffRegular /> : <EyeRegular />}
            </button>
          </div>
          
          <Button
            appearance="primary"
            size="large"
            onClick={handleSignIn}
            disabled={loading}
            icon={<PersonRegular />}
            className={styles.signInButton}
            type="submit"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default Login; 